import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, Loader2, ArrowRight, CalendarCheck } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ConfirmBookingPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [state, setState] = useState('loading');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Foglalás megerősítése | Orosz Coaching';
    if (!token) {
      setState('error');
      setError('Hiányzó megerősítő kód. Kérlek nyisd meg a linket az e-mailből.');
      return;
    }
    (async () => {
      try {
        const res = await axios.post(`${API}/consultations/confirm`, { token });
        setData(res.data);
        setState('ok');
      } catch (err) {
        setError(
          err?.response?.data?.detail ||
            'A megerősítés nem sikerült. Kérlek próbáld újra, vagy foglalj új időpontot.'
        );
        setState('error');
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center px-6 py-16">
      <div
        data-testid="confirm-booking-card"
        className="w-full max-w-lg bg-gray-900 border border-gray-800 rounded-2xl p-8 text-center"
      >
        <Link to="/" className="inline-block mb-8 font-bold tracking-widest" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          <span className="text-yellow-400">OROSZ</span> <span className="text-white">COACHING</span>
        </Link>

        {state === 'loading' && (
          <div data-testid="confirm-loading">
            <Loader2 className="mx-auto mb-4 animate-spin text-yellow-500" size={44} />
            <p className="text-gray-300">Megerősítés folyamatban…</p>
          </div>
        )}

        {state === 'ok' && (
          <div data-testid="confirm-success">
            <CheckCircle2 className="mx-auto mb-4 text-emerald-500" size={56} />
            <h1 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              {data?.already_confirmed ? 'Ez az időpont már megerősítve van' : 'Időpontod véglegesítve!'}
            </h1>
            {data?.slot && (
              <div className="inline-flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 mb-5">
                <CalendarCheck className="text-yellow-500" size={18} />
                <span className="font-semibold">
                  {data.slot.date} · {data.slot.time}
                </span>
                <span className="text-gray-400 text-sm">
                  ({data.slot.duration_minutes === 20 ? '20 perc' : '60 perc'})
                </span>
              </div>
            )}
            <p className="text-gray-300 mb-2">
              Helyszín: BB Gym Miskolc, 3526 Miskolc, Szeles u. 71.
            </p>
            <p className="text-sm text-gray-400 mb-8">
              Küldtünk egy visszaigazoló e-mailt is. Ha közbejön valami, válaszolj rá, és egyeztetünk új időpontot.
            </p>
            <Button
              asChild
              data-testid="confirm-home-btn"
              className="bg-yellow-600 hover:bg-yellow-500 text-gray-900 font-bold"
            >
              <Link to="/">
                Vissza a főoldalra <ArrowRight className="ml-2" size={16} />
              </Link>
            </Button>
          </div>
        )}

        {state === 'error' && (
          <div data-testid="confirm-error">
            <XCircle className="mx-auto mb-4 text-red-500" size={56} />
            <h1 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Nem sikerült a megerősítés
            </h1>
            <p className="text-gray-300 mb-8">{error}</p>
            <Button
              asChild
              data-testid="confirm-rebook-btn"
              className="bg-yellow-600 hover:bg-yellow-500 text-gray-900 font-bold"
            >
              <Link to="/#contact">
                Új időpont foglalása <ArrowRight className="ml-2" size={16} />
              </Link>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
